import { ReactNode, useEffect, useState } from 'react';
import { OrderPaymentMethod } from '../../clients/quipli-service/order/order';
import { useAPI } from '../../clients/quipli-service/quipli-service-client';
import { OnAccount, OnAccountStatus } from '../../clients/quipli-service/payment/on-account/on-account';
import { Spinner } from '../../components/spinner/spinner';
import { Typography } from '../../components/typography/typography';
import { Badge } from '../../components/badge/badge';
import { EditableField } from '../../components/field/field';
import { Button } from '../../components/form/button';
import { OnAccountForm } from '../customer/page/tabs/payment-methods';
import { User } from '../../clients/quipli-service/user/user';
import Icon from '../../components/icons/icon';
import { Radio } from '../../components/radio/radio';
import StripeSetupInit from './stripe/setup-component/stripe-setup-init';
import { Configuration } from '../../clients/quipli-service/configuration/configuration';
import { Location } from '../../clients/quipli-service/location/location';
import { StripeCard } from '../../clients/quipli-service/payment/payment';
import { TerminalSelect } from './stripe/terminal-component/terminal-select';
import { usePaymentApiV1 } from '../../clients/quipli-service/payment/payment-api-v1';
import { ConfirmModal } from '../../components/modal/confirm-modal';
import { toast } from '../../util/toasts';
import { DateTime } from 'luxon';
import { StripeBankAccount } from '../../clients/quipli-service/payment/payment-v2';
import { usePaymentApiV2 } from '../../clients/quipli-service/payment/payment-api-v2';
import { usePaymentConfigApiV1 } from '../../clients/quipli-service/payment-config/payment-config-api-v1';
import { usePayment } from '../../hooks/use-payment-context';
import {
  PaymentMethodConfigType,
  PaymentMethodStatus,
} from '../../clients/quipli-service/payment-config/payment-config';

export const OrderPaymentMethodSelector = ({
  customer,
  paymentMethod,
  location,
  poNumber,
  allowedPaymentMethods = Object.values(OrderPaymentMethod),
  setPaymentMethod,
  setCardId,
  card,
  setCard,
  setPoNumber,
  setBankAccountId,
  zIndex = 1002,
  showTerminal = true,
  setOnAccount,
  setCustomPaymentMethod,
  customPaymentMethod,
}: {
  customer: User | null;
  paymentMethod?: OrderPaymentMethod;
  location: Location | null;
  poNumber?: string;
  allowedPaymentMethods?: OrderPaymentMethod[];
  setPaymentMethod: (p: OrderPaymentMethod) => void;
  setCardId: (cardId: number) => void;
  card?: StripeCard;
  setCard?: (card: StripeCard) => void;
  setPoNumber: (poNumber: string) => void;
  setBankAccountId: (bankAccountId: string) => void;
  zIndex?: number;
  showTerminal?: boolean;
  setOnAccount?: (onAccount?: OnAccount) => void;
  setCustomPaymentMethod: (customPaymentMethod: string) => void;
  customPaymentMethod?: string;
}) => {
  const { getMultipleConfigurations, getUserBankDetail } = useAPI();
  const { getStripeAccount } = usePaymentConfigApiV1();
  const { listTerminals } = usePaymentApiV1();
  const [conf, setConf] = useState<Configuration[]>([]);
  const [terminalOnline, setTerminalOnline] = useState<boolean>(false);
  const [stripeEnabled, setStripeEnabled] = useState<boolean>(true);
  const [bankAccount, setBankAccount] = useState<StripeBankAccount>();
  const paymentContext = usePayment();

  useEffect(() => {
    const loadConf = async () => {
      const configurations = await getMultipleConfigurations([
        'CONF_RENT_ON_ACCOUNT',
        'CONF_ORDER_NO_PAYMENT',
        'CONF_ENABLE_ACH',
      ]);
      setConf(configurations);
      const defaultMethod = Object.values(OrderPaymentMethod).find((p) =>
        paymentContext?.paymentMethods
          .filter((_) => !_.deletedAt && _.status == PaymentMethodStatus.Active)
          .find((pm) => {
            switch (p) {
              case OrderPaymentMethod.CreditCard:
                return pm.name === 'card';
              case OrderPaymentMethod.ProceedWithoutPayment:
                return pm.name === 'none';
              case OrderPaymentMethod.BankAccount:
                return pm.name === 'bank';
              case OrderPaymentMethod.OnAccount:
                return pm.name === 'on_account';
              case OrderPaymentMethod.Cash:
                return pm.name === 'cash';
              case OrderPaymentMethod.Check:
                return pm.name === 'check';
              default:
                return false;
            }
          }),
      );
      setPaymentMethod(defaultMethod || OrderPaymentMethod.ProceedWithoutPayment);
    };
    loadConf();
  }, []);

  const loadBankDetail = async () => {
    if (customer) {
      try {
        const account = await getUserBankDetail(customer.id);
        if (account && account.status === OnAccountStatus.Approved) {
          setPaymentMethod(OrderPaymentMethod.OnAccount);
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  useEffect(() => {
    loadBankDetail();
  }, [customer]);

  useEffect(() => {
    const checkTerminalOnline = async () => {
      const stripeAccount = await getStripeAccount();
      if (stripeAccount) {
        if (!location) {
          return;
        }
        const terminals = await listTerminals(location.id);
        const online = terminals.some((t) => {
          return t.status == 'online';
        });
        setTerminalOnline(online);
      } else {
        setStripeEnabled(false);
      }
    };

    checkTerminalOnline();
  }, [location]);

  const setTerminal = () => {
    setPaymentMethod(OrderPaymentMethod.Terminal);
  };

  return customer && paymentContext ? (
    <>
      <div className="flex gap-[24px] flex-wrap">
        {allowedPaymentMethods.includes(OrderPaymentMethod.CreditCard) &&
          paymentContext?.paymentMethods.find((_) => _.name == 'card') &&
          stripeEnabled && (
            <Radio
              name="order-payment-method"
              isChecked={paymentMethod === OrderPaymentMethod.CreditCard}
              onChange={(isChecked) => isChecked && setPaymentMethod(OrderPaymentMethod.CreditCard)}
              label="Credit/Debit Card"
              type="white"
              dataCy="order-payment-method-credit-card"
            />
          )}

        {allowedPaymentMethods.includes(OrderPaymentMethod.Terminal) &&
          paymentContext?.paymentMethods.find((_) => _.name == 'card') &&
          stripeEnabled &&
          location?.allowStripeTerminal &&
          terminalOnline && (
            <Radio
              name="order-payment-method"
              isChecked={paymentMethod === OrderPaymentMethod.Terminal}
              onChange={(isChecked) => {
                if (isChecked) {
                  setTerminal();
                }
              }}
              label="Swipe Card"
              type="white"
              dataCy="order-payment-method-terminal"
            />
          )}
        {allowedPaymentMethods.includes(OrderPaymentMethod.CreditCard) &&
          paymentContext?.paymentMethods.find((_) => _.name == 'bank') &&
          stripeEnabled &&
          conf.find((c) => c.variable === 'CONF_ENABLE_ACH')?.value === '1' && (
            <Radio
              name="order-payment-method"
              isChecked={paymentMethod === OrderPaymentMethod.BankAccount}
              onChange={(isChecked) => isChecked && setPaymentMethod(OrderPaymentMethod.BankAccount)}
              label="Bank Account"
              type="white"
              dataCy="order-payment-method-bank-account"
            />
          )}

        {allowedPaymentMethods.includes(OrderPaymentMethod.OnAccount) &&
          paymentContext?.paymentMethods.find((_) => _.name == 'on_account') &&
          conf.find((c) => c.variable === 'CONF_RENT_ON_ACCOUNT')?.value === '1' && (
            <Radio
              name="order-payment-method"
              isChecked={paymentMethod === OrderPaymentMethod.OnAccount}
              onChange={(isChecked) => isChecked && setPaymentMethod(OrderPaymentMethod.OnAccount)}
              label="Bill my account"
              type="white"
              dataCy="order-payment-method-on-account"
            />
          )}

        {allowedPaymentMethods.includes(OrderPaymentMethod.Cash) &&
          paymentContext?.paymentMethods.find((_) => _.name == 'cash') && (
            <Radio
              name="order-payment-method"
              isChecked={paymentMethod === OrderPaymentMethod.Cash}
              onChange={(isChecked) => isChecked && setPaymentMethod(OrderPaymentMethod.Cash)}
              label="Cash"
              type="white"
              dataCy="order-payment-method-cash"
            />
          )}

        {allowedPaymentMethods.includes(OrderPaymentMethod.Check) &&
          paymentContext?.paymentMethods.find((_) => _.name == 'check') && (
            <Radio
              name="order-payment-method"
              isChecked={paymentMethod === OrderPaymentMethod.Check}
              onChange={(isChecked) => isChecked && setPaymentMethod(OrderPaymentMethod.Check)}
              label="Check"
              type="white"
              dataCy="order-payment-method-check"
            />
          )}
        {paymentContext.paymentMethods
          .filter((_) => _.type == PaymentMethodConfigType.Custom)
          .map((pm) => {
            return (
              <Radio
                name="order-payment-method"
                isChecked={paymentMethod === OrderPaymentMethod.Custom && customPaymentMethod === pm.name}
                onChange={(isChecked) => {
                  if (isChecked) {
                    setPaymentMethod(OrderPaymentMethod.Custom);
                    setCustomPaymentMethod(pm.name);
                  }
                }}
                label={pm.displayName}
                type="white"
                dataCy="order-payment-method-custom"
              />
            );
          })}

        {allowedPaymentMethods.includes(OrderPaymentMethod.ProceedWithoutPayment) &&
          conf.find((c) => c.variable === 'CONF_ORDER_NO_PAYMENT')?.value === '1' && (
            <Radio
              name="order-payment-method"
              isChecked={paymentMethod === OrderPaymentMethod.ProceedWithoutPayment}
              onChange={(isChecked) => isChecked && setPaymentMethod(OrderPaymentMethod.ProceedWithoutPayment)}
              label="Skip for now"
              type="white"
              dataCy="order-payment-method-proceed-without-payment"
            />
          )}
      </div>

      <div className="pt-[8px] pb-[12px]">
        {paymentMethod !== OrderPaymentMethod.OnAccount && <PoNumber poNumber={poNumber} setPoNumber={setPoNumber} />}

        {paymentMethod === OrderPaymentMethod.CreditCard ? (
          <CardPaymentMethod customer={customer} setCardId={setCardId} card={card} setCard={setCard} />
        ) : paymentMethod === OrderPaymentMethod.OnAccount ? (
          <OnAccountPaymentMethod
            customer={customer}
            defaultValue={poNumber}
            setPoNumber={setPoNumber}
            setOnAccount={setOnAccount}
            zIndex={zIndex}
          />
        ) : paymentMethod === OrderPaymentMethod.Terminal ? (
          <TerminalPayment
            customer={customer}
            location={location}
            toggleTerminal={() => {
              setPaymentMethod(OrderPaymentMethod.CreditCard);
            }}
            setCardId={setCardId}
            setCard={(card) => {
              setCard && setCard(card);
              setPaymentMethod(OrderPaymentMethod.CreditCard);
            }}
            showTerminal={showTerminal}
            setPaymentMethod={setPaymentMethod}
          />
        ) : paymentMethod === OrderPaymentMethod.BankAccount ? (
          <BankPaymentMethodInput
            customer={customer}
            setBankAccountId={setBankAccountId}
            bankAccount={bankAccount}
            setBankAccount={setBankAccount}
          ></BankPaymentMethodInput>
        ) : (
          <></>
        )}
      </div>
    </>
  ) : null;
};

const PoNumber = ({ poNumber, setPoNumber }: { poNumber?: string; setPoNumber: (poNumber: string) => void }) => {
  const [value, setValue] = useState<string>(poNumber || '');

  return (
    <div className="flex flex-col gap-[14px] justify-between">
      <Typography type="text-sm" className="text-grey-500">
        PO number
      </Typography>

      <EditableField
        type="text"
        label="PO Number"
        noValueText="N/A"
        placeholder="Number"
        value={value}
        onChange={(newValue) => {
          setValue(newValue);
          setPoNumber(newValue);
        }}
      />
    </div>
  );
};

const OnAccountPaymentMethod = ({
  defaultValue,
  customer,
  setPoNumber,
  zIndex = 1002,
  setOnAccount,
}: {
  defaultValue?: string;
  customer: User;
  setPoNumber: (poNumber: string) => void;
  zIndex?: number;
  setOnAccount?: (onAccount?: OnAccount) => void;
}) => {
  const { getUserBankDetail } = useAPI();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [account, setAccount] = useState<OnAccount>();
  const [value, setValue] = useState<string>(defaultValue || '');

  const loadBankDetail = async () => {
    if (customer) {
      setIsLoading(true);
      try {
        const bankDetail = await getUserBankDetail(customer.id);
        setAccount(bankDetail);
        setOnAccount?.(bankDetail);
      } catch (error) {
        console.error(error);
      }

      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadBankDetail();
  }, [customer]);

  return isLoading ? (
    <Spinner />
  ) : account ? (
    <div className="flex gap-[24px]">
      <div className="flex flex-col gap-[14px] justify-between">
        <Typography type="text-sm" className="text-grey-500">
          Account status
        </Typography>
        <Badge
          text={account.status}
          type={
            account.status === OnAccountStatus.Approved
              ? 'success'
              : account.status === OnAccountStatus.Declined
              ? 'danger'
              : 'default'
          }
        />
      </div>

      <div className="flex flex-col gap-[14px] justify-between">
        <Typography type="text-sm" className="text-grey-500">
          Account number
        </Typography>
        <Typography type="text-sm" className="text-quipli-black-800">
          {account.accountNumber}
        </Typography>
      </div>

      <div className="flex flex-col gap-[14px] justify-between">
        <Typography type="text-sm" className="text-grey-500">
          PO number
        </Typography>

        <EditableField
          type="text"
          label="PO Number"
          noValueText="N/A"
          placeholder="Number"
          value={value}
          onChange={(newValue) => {
            setValue(newValue);
            setPoNumber(newValue);
          }}
        />
      </div>
    </div>
  ) : (
    <div>
      <OnAccountForm user={customer} onAccount={account} onSubmit={() => loadBankDetail()} zIndex={zIndex} />
    </div>
  );
};

const CardPaymentMethod = ({
  customer,
  setCardId,
  defaultCardLast4,
  card,
  setCard,
}: {
  customer: User;
  defaultCardLast4?: string;
  setCardId: (card: number) => void;
  card?: StripeCard;
  setCard?: (stripeCard: StripeCard) => void;
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [userCards, setUserCards] = useState<StripeCard[]>([]);
  const [paymentCard, setPaymentCard] = useState<StripeCard | null>(null);
  const [selectionForm, setSelectionForm] = useState<'cardSelect' | 'stripeSetup' | 'stripeSetupEdit'>('cardSelect');
  const [selectedCard, setSelectedCard] = useState<StripeCard>();
  const [defaultCard, setDefaultCard] = useState<StripeCard>();
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<ReactNode>();

  const { deleteCardAdmin, getCardsForUser } = usePaymentApiV1();

  useEffect(() => {
    loadUserCards();
  }, [customer, selectionForm]);

  const loadUserCards = async () => {
    if (customer) {
      setIsLoading(true);
      try {
        const cards: StripeCard[] = [];
        cards.push(...(await getCardsForUser(customer.id)));
        if (card && card.deletedAt) {
          cards.push(card);
        }
        if (cards.length === 0) {
          setSelectionForm('stripeSetup');
        } else {
          let defaultCard = cards.find((c) => c.last4 === defaultCardLast4);
          if (!defaultCard && card) {
            defaultCard = card;
          }
          setDefaultCard(defaultCard);
          setUserCards(cards);
          setPaymentCard(defaultCard || cards[0]);
          setCardId(defaultCard?.id || cards[0].id);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleOnDelete = async (cardId: number) => {
    try {
      await deleteCardAdmin(cardId, customer.id);
      toast('Card removed.', 'success');
      setDeleteConfirmModal(undefined);
      loadUserCards();
    } catch (error) {
      toast('Error removing card.', 'error');
    }
  };

  const CardSelect = () => (
    <>
      {isLoading ? (
        <Spinner />
      ) : (
        <div className="grid grid-cols-1 mb-[16px] gap-[16px]">
          <>
            {userCards.map((card, index) => {
              let disabled = false;
              let disabledLabel: string | undefined = undefined;
              if (card.lockTimestamp) {
                const duration = DateTime.now().diff(card.lockTimestamp, ['minutes', 'seconds']);
                // past
                disabled = duration.minutes <= 30 && duration.minutes > 0;
                disabledLabel = card.lockTimestamp.plus({ minutes: 30 }).toFormat('HH:mm');
              }
              let icon = undefined;
              switch (card.brand.toLowerCase().trim()) {
                case 'visa':
                  icon = <Icon icon={'VisaCardLogo'} size={'xlarge'}></Icon>;
                  break;
                case 'mastercard':
                  icon = <Icon icon={'MasterCardLogo'} size={'xlarge'}></Icon>;
                  break;
                case 'amex':
                case 'americanexpress':
                  icon = <Icon icon={'AmericanExpressLogo'} size={'xlarge'}></Icon>;
                  break;
              }

              return (
                <>
                  <div
                    key={`card-user-title-${index}`}
                    className="flex flex-wrap justify-between items-center border border-x-0 border-b-0 border-solid border-grey-200 px-[12px] pt-[6px]"
                    tabIndex={0}
                  >
                    <div className="flex items-center gap-[8px]">
                      <Typography type="text-md" weight="medium" className="text-[#2B2B2B]">
                        {card.nickname ? card.nickname + ' ' : ''}
                        {card.cardholderName}
                      </Typography>
                      <span className="text-[#2B2B2B]">••••</span>
                      <Typography type="text-md" weight="semi-bold" className="text-[#2B2B2B]">
                        {card.last4}
                      </Typography>
                      {icon}
                    </div>
                    {!card.deletedAt && (
                      <div
                        className="cursor-pointer"
                        onClick={() => {
                          setSelectedCard(card);
                          setSelectionForm('stripeSetupEdit');
                        }}
                      >
                        <Typography type="text-md" weight="medium" className="text-[var(--link-200)]">
                          Edit
                        </Typography>
                      </div>
                    )}
                  </div>
                  <div
                    key={`card-user-body-${index}`}
                    className="flex flex-wrap justify-between px-[12px]"
                    tabIndex={1}
                  >
                    {!disabled ? (
                      <Radio
                        isChecked={paymentCard?.id === card.id}
                        name={`payment_card_${index}`}
                        onChange={(isChecked) => {
                          if (isChecked) {
                            setPaymentCard(card);
                            setCardId(card.id);
                          }
                        }}
                        label={defaultCard?.id === card.id ? 'Default payment method' : 'Set as default payment'}
                      ></Radio>
                    ) : (
                      <Typography type={'text-sm'}>Disabled until {disabledLabel}</Typography>
                    )}

                    <div
                      className="cursor-pointer"
                      onClick={(e) => {
                        e.preventDefault();
                        setDeleteConfirmModal(
                          <ConfirmModal
                            prompt="Remove card?"
                            additionalText="This cannot be undone"
                            noLabel="Cancel"
                            yesLabel="Remove"
                            yesButtonYellow
                            isOpen={true}
                            headerIcon={
                              <div className="flex justify-center">
                                <Icon icon="InfoCircle" size="xlarge" stroke="var(--error-500)" />
                              </div>
                            }
                            onConfirm={() => handleOnDelete(card.id)}
                            close={() => setDeleteConfirmModal(undefined)}
                            zIndex={10000}
                          />,
                        );
                      }}
                    >
                      <Icon icon="Trash" />
                    </div>
                  </div>
                </>
              );
            })}
          </>
        </div>
      )}

      {deleteConfirmModal}

      <div className="flex flex-wrap justify-center items-center border border-x-0 border-b-0 border-solid border-grey-200 px-[12px] pt-[12px]">
        <Button
          className="button-primary"
          onClick={() => {
            setSelectionForm('stripeSetup');
          }}
        >
          Add new card
        </Button>
      </div>
    </>
  );

  return (
    <>
      {selectionForm == 'cardSelect' && <CardSelect />}
      {selectionForm == 'stripeSetup' && (
        <StripeSetupInit
          userId={customer.id}
          close={() => setSelectionForm('cardSelect')}
          setCard={(card) => {
            setCard && setCard(card);
          }}
          type={'card'}
        />
      )}
      {selectionForm == 'stripeSetupEdit' && (
        <StripeSetupInit
          userId={customer.id}
          close={() => setSelectionForm('cardSelect')}
          userCard={selectedCard}
          setCard={setCard}
          type={'card'}
        />
      )}
    </>
  );
};

const BankPaymentMethodInput = ({
  customer,
  setBankAccountId,
  bankAccount,
  setBankAccount,
}: {
  customer: User;
  setBankAccountId: (bankAccountId: string) => void;
  bankAccount?: StripeBankAccount;
  setBankAccount?: (account: StripeBankAccount) => void;
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [userBankAccounts, setUserBankAccounts] = useState<StripeBankAccount[]>([]);
  const [paymentBankAccount, setPaymentBankAccount] = useState<StripeBankAccount | null>(null);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<ReactNode>();

  const { searchBankAccounts, deleteBankAccount } = usePaymentApiV2();
  const { sendEmailAsync } = useAPI();

  useEffect(() => {
    loadUserBankAccounts();
  }, [customer]);

  useEffect(() => {
    if (paymentBankAccount) {
      setBankAccountId(paymentBankAccount.id);
      setBankAccount && setBankAccount(paymentBankAccount);
    }
  }, [paymentBankAccount]);

  const loadUserBankAccounts = async () => {
    setIsLoading(true);
    try {
      const [activeBankAccounts, defaultBankAccounts] = await Promise.all([
        searchBankAccounts({ userIds: [customer.id] }),
        bankAccount ? searchBankAccounts({ ids: [bankAccount.id] }) : Promise.resolve(),
      ]);
      const defaultBankAccount = defaultBankAccounts && defaultBankAccounts.find((b) => b);

      if (activeBankAccounts.length === 0 && !defaultBankAccount) {
        setPaymentBankAccount(null);
      } else {
        let bankAccounts = activeBankAccounts;
        if (defaultBankAccount && !activeBankAccounts.some((c) => c.id === defaultBankAccount.id)) {
          bankAccounts = [defaultBankAccount, ...activeBankAccounts];
        }
        setUserBankAccounts(bankAccounts);
        setPaymentBankAccount(defaultBankAccount || bankAccounts[0]);
      }
    } catch (error) {
      toast('Failed to load user bank accounts', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOnDelete = async (bankAccountId: string) => {
    try {
      await deleteBankAccount(bankAccountId, customer.id);
      toast('Account removed.', 'success');
      setDeleteConfirmModal(undefined);
      loadUserBankAccounts();
    } catch (error) {
      toast('Error removing account.', 'error');
    }
  };
  const sendBankLink = async () => {
    // send async email to customer__ach_account payment link
    await sendEmailAsync({ templateName: 'customer__bank_account_setup', userId: customer.id });
    toast('Email sent.', 'success');
  };

  const BankAccountSelect = () => (
    <>
      {isLoading ? (
        <Spinner />
      ) : (
        <div className="grid grid-cols-1 mb-[16px] gap-[16px]">
          {userBankAccounts
            .filter((b) => !b.deletedAt)
            .map((account, index) => {
              const disabled = account.status == 'unverified';
              return (
                <>
                  <div
                    key={`account-user-title-${index}`}
                    className="flex justify-between grow gap-2 border border-x-0 border-b-0 border-solid border-grey-200 px-[12px] pt-[6px]"
                    tabIndex={0}
                  >
                    <div className="flex items-center gap-2">
                      <Typography weight={'semi-bold'} type={'text-md'}>
                        {'Account ending'} •••• {account.last4}
                      </Typography>
                    </div>
                    <div className="flex items-center gap-2">
                      <div
                        className="cursor-pointer"
                        onClick={(e) => {
                          e.preventDefault();
                          setDeleteConfirmModal(
                            <ConfirmModal
                              prompt="Remove account?"
                              additionalText="This cannot be undone"
                              noLabel="Cancel"
                              yesLabel="Remove"
                              yesButtonYellow
                              isOpen={true}
                              headerIcon={
                                <div className="flex justify-center">
                                  <Icon icon="InfoCircle" size="xlarge" stroke="var(--error-500)" />
                                </div>
                              }
                              onConfirm={() => handleOnDelete(account.id)}
                              close={() => setDeleteConfirmModal(undefined)}
                              zIndex={10000}
                            />,
                          );
                        }}
                      >
                        <Icon icon="Trash" />
                      </div>
                    </div>
                  </div>
                  <div key={`card-user-title-${index}`} className="" tabIndex={1}>
                    {!disabled ? (
                      <Radio
                        isChecked={paymentBankAccount?.id === account.id}
                        name={`payment_account_${index}`}
                        onChange={(isChecked) => {
                          if (isChecked) {
                            setPaymentBankAccount(account);
                          }
                        }}
                        label={bankAccount?.id == account.id ? 'Default bank account' : 'Set as default bank account'}
                      ></Radio>
                    ) : (
                      <div className="flex justify-between gap-2">
                        <span>Unverified</span>
                      </div>
                    )}
                  </div>
                </>
              );
            })}
          <div className={'flex flex-column justify-items-center gap-2'}>
            {userBankAccounts.length === 0 ? <span>There are no bank accounts configured</span> : <></>}
            <Button className={'button button-primary'} onClick={sendBankLink}>
              <Typography type="text-sm">Send Bank Account setup link</Typography>
            </Button>
          </div>
        </div>
      )}

      {deleteConfirmModal}
    </>
  );

  return (
    <>
      <BankAccountSelect />
    </>
  );
};

const TerminalPayment = ({
  customer,
  location,
  toggleTerminal,
  setCardId,
  setCard,
  showTerminal,
  setPaymentMethod,
}: {
  customer: User;
  location: Location | null;
  toggleTerminal: () => void;
  setCardId: (cardId: number) => void;
  setCard?: (stripeCard: StripeCard) => void;
  setPaymentMethod: (paymentMethod: OrderPaymentMethod) => void;
  showTerminal: boolean;
}) => {
  return location && showTerminal ? (
    <>
      <TerminalSelect
        locationId={location.id}
        user={customer}
        close={() => toggleTerminal()}
        setCardId={setCardId}
        setCard={setCard}
        method={'setup'}
        setMethod={() => {}}
        showFacilitator={false}
        onComplete={() => {
          setPaymentMethod(OrderPaymentMethod.CreditCard);
        }}
        showSetupButton={true}
      />
    </>
  ) : !showTerminal ? (
    <Typography type={'text-md'}>You may scan the card at the end of checkout</Typography>
  ) : null;
};
